import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Hash, Sparkles, Calendar, Sun, Moon, Star, Lock, ArrowRight, LogIn, LogOut, User, ChevronLeft, Brain, Heart, Compass, Clock, History, Bell } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import PlaylistToggle from '@/components/PlaylistToggle';
import QuizDiscoverChatSection from '@/components/QuizDiscoverChatSection';

const MODULES = [
  {
    id: 'theme',
    name: 'Thème Fondamental',
    description: 'Découvrez vos nombres clés : chemin de vie, expression, âme',
    icon: Brain,
    path: '/numerologia/theme',
    isFree: true,
    features: ['Chemin de vie', 'Nombre d\'expression', 'Nombre intime', 'Nombre de réalisation']
  },
  {
    id: 'cycles',
    name: 'Cycles & Météo',
    description: 'Votre année, mois et jour personnels. Anticipez les énergies.',
    icon: Calendar,
    path: '/numerologia/cycles',
    isFree: false,
    features: ['Année personnelle', 'Mois personnel', 'Prévisions mensuelles', 'Cycle de 9 ans']
  },
  {
    id: 'daily',
    name: 'Nombres du Quotidien',
    description: 'Heures miroir, numéros répétitifs, adresses, téléphones...',
    icon: Clock,
    path: '/numerologia/daily',
    isFree: false,
    features: ['Heures miroir', 'Nombres répétitifs', 'Analyse de numéros', 'Messages quotidiens']
  },
  {
    id: 'karma',
    name: 'Karma & Évolution',
    description: 'Dettes karmiques, leçons de vie, chemin d\'évolution spirituelle',
    icon: Compass,
    path: '/numerologia/karma',
    isFree: false,
    features: ['Dettes karmiques', 'Nombres manquants', 'Forces dominantes', 'Conseils d\'évolution']
  }
];

const ModuleCard = ({ module, index, onSelect, isPremium }) => {
  const Icon = module.icon;
  const isAvailable = module.isFree || isPremium;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className={`group ${isAvailable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
      onClick={() => isAvailable && onSelect(module)}
      data-testid={`module-card-${module.id}`}
    >
      <div className={`
        relative p-6 rounded-2xl border transition-all duration-300
        ${isAvailable 
          ? 'bg-white border-emerald-200 hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-100' 
          : 'bg-gray-50 border-gray-200 opacity-75'}
      `}>
        {/* Badge */}
        <div className="absolute top-4 right-4">
          {module.isFree ? (
            <span className="px-2 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full">
              Gratuit
            </span>
          ) : (
            <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
              <Lock className="h-3 w-3" />
              Premium
            </span>
          )}
        </div>
        
        {/* Icon */}
        <div className={`
          w-14 h-14 rounded-xl flex items-center justify-center mb-4
          ${isAvailable ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gray-300'}
        `}>
          <Icon className="h-7 w-7 text-white" />
        </div>
        
        {/* Content */}
        <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
          {module.name}
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          {module.description}
        </p>
        
        {/* Features */}
        <ul className="space-y-1 mb-4">
          {module.features.slice(0, 3).map((feature, i) => (
            <li key={i} className="text-xs text-gray-500 flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-emerald-400" />
              {feature}
            </li>
          ))}
        </ul>
        
        {/* Action */}
        {isAvailable ? (
          <Button 
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid={`explore-module-${module.id}`}
          >
            Explorer
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button 
            variant="outline" 
            className="w-full border-amber-300 text-amber-600 rounded-xl"
            disabled
          >
            <Lock className="mr-2 h-4 w-4" />
            Débloquer
          </Button>
        )}
      </div>
    </motion.div>
  );
};

const NumerologiaHomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, isLoading } = useAuth();
  const [isPremium] = useState(false); // TODO: Connect to subscription system

  const handleLogout = async () => {
    await logout();
  };

  const handleModuleSelect = (module) => {
    navigate(module.path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
              data-testid="back-to-reveliae"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="font-heading text-lg font-bold tracking-wide bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">
                REVELIAE
              </span>
            </button>
            <span className="text-gray-300">/</span>
            <div className="flex items-center gap-2">
              <Hash className="h-5 w-5 text-emerald-600" />
              <span className="font-heading text-xl font-bold tracking-wide text-emerald-700">
                NUMEROLOGIA
              </span>
            </div>
          </div>
          
          {!isLoading && (
            <div className="flex items-center gap-4">
              {isAuthenticated && user ? (
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/numerologia/alerts')}
                    className="rounded-full text-amber-600 hover:bg-amber-50"
                    data-testid="numerologia-alerts-btn"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Alertes
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/numerologia/history')}
                    className="rounded-full text-emerald-600 hover:bg-emerald-50"
                    data-testid="numerologia-history-btn"
                  >
                    <History className="h-4 w-4 mr-2" />
                    Historique
                  </Button>
                  <div className="flex items-center gap-2 bg-emerald-50 px-4 py-2 rounded-full border border-emerald-200">
                    <User className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-800">{user.first_name || user.name}</span>
                  </div>
                  <Button
                    data-testid="numerologia-logout-btn"
                    onClick={handleLogout}
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-gray-600 hover:text-emerald-600 hover:bg-emerald-50"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Déconnexion
                  </Button>
                </div>
              ) : (
                <Button
                  data-testid="numerologia-login-btn"
                  onClick={() => navigate('/auth')}
                  variant="outline"
                  size="sm"
                  className="rounded-full border-emerald-400 text-emerald-600 hover:bg-emerald-50"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Connexion
                </Button>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-28 pb-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-6">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Numerology Engine</span>
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6">
            <span className="text-gray-900">Découvrez le pouvoir</span>
            <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mt-2">
              des nombres
            </span>
          </h1>
          
          <p className="text-gray-600 text-lg max-w-xl mx-auto leading-relaxed">
            Analyse numérologique complète basée sur votre nom et date de naissance.
            Comprenez votre chemin de vie, vos cycles et votre karma.
          </p>
        </motion.div>
      </div>

      {/* Modules Grid */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {MODULES.map((module, index) => (
            <ModuleCard
              key={module.id}
              module={module}
              index={index}
              onSelect={handleModuleSelect}
              isPremium={isPremium}
            />
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="bg-emerald-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="font-heading text-2xl font-bold mb-4">
              La numérologie : un outil d'évolution
            </h2>
            <p className="text-emerald-200 max-w-2xl mx-auto mb-8">
              La numérologie n'est pas une science exacte, mais un outil de réflexion et de développement personnel.
              Elle offre des clés pour mieux se comprendre et orienter ses choix de vie.
            </p>
            
            <div className="grid grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Sun className="h-6 w-6 text-emerald-300" />
                </div>
                <p className="text-sm text-emerald-200">Structuré & Méthodique</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Heart className="h-6 w-6 text-emerald-300" />
                </div>
                <p className="text-sm text-emerald-200">Bienveillant & Positif</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Star className="h-6 w-6 text-emerald-300" />
                </div>
                <p className="text-sm text-emerald-200">Orienté Évolution</p>
              </div>
            </div>

            {/* Quick Actions - Quiz, Knowledge, Chat */}
            <QuizDiscoverChatSection
              theme="emerald"
              appName="numerologia"
              quizPath="/numerologia/quiz"
              quizTitle="Quiz Numérologie"
              quizDescription="Testez vos connaissances sur les nombres"
              quizFeatures={['20 questions', 'Explications détaillées', 'Score final']}
              discoverPath="/numerologia/knowledge"
              discoverTitle="Découvrir"
              discoverDescription="Apprenez les fondamentaux de la numérologie"
              discoverFeatures={['Les nombres de 1 à 9', 'Significations', 'Calculs de base']}
              discoverIsPremium={true}
              chatPath="/numerologia/chat"
              chatTitle="Poser une Question"
              chatDescription="Discutez avec Pythia, notre experte IA"
              chatExpertName="Pythia"
              chatFeatures={['Chat IA', 'Réponses personnalisées', 'Conseils numériques']}
            />
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-8 text-center">
        <p className="text-gray-500 text-sm">
          Un produit de <span className="text-emerald-600 font-medium">C4U Aventures</span>
        </p>
        <p className="text-gray-400 text-xs mt-2">
          © 2026 REVELIAE • NUMEROLOGIA
        </p>
      </div>

      {/* Playlist Toggle */}
      <PlaylistToggle theme="emerald" app="numerologia" />
    </div>
  );
};

export default NumerologiaHomePage;
